/**
 * Waitlist closer.
 *
 *   - Left  : eyebrow / title / lede / meta rows
 *   - Right : form (favorite artist + email) + honeypot + submit
 *
 * Form lifecycle (Animation Spec §4.5):
 *   idle → submitting → success | error
 *
 * Calls POST /api/waitlist with { favoriteArtist, email, hp }. Honeypot field "company"
 * is hidden from real users; bots filling it get a silent 200 (no enqueue).
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Reveal } from './primitives/Reveal';
import { useTrack } from '@/analytics';
import { waitlistCopy } from '@/copy/waitlist';

const FormSchema = z.object({
  favoriteArtist: z.string().trim().max(120).optional(),
  email: z.string().trim().email(waitlistCopy.errorEmail),
  // Honeypot — bots love filling text fields named "company".
  company: z.string().max(0, 'spam').optional(),
});
type FormValues = z.infer<typeof FormSchema>;

type FormState = 'idle' | 'sending' | 'success' | 'error';

export function Waitlist(): JSX.Element {
  const [state, setState] = useState<FormState>('idle');
  const [serverError, setServerError] = useState<string | null>(null);
  const track = useTrack();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    mode: 'onSubmit',
  });

  // Auto-reset error state once the user starts typing again.
  useEffect(() => {
    if (state !== 'error') return;
    const t = setTimeout(() => setServerError(null), 4500);
    return () => clearTimeout(t);
  }, [state]);

  const onSubmit = async (values: FormValues) => {
    setState('sending');
    setServerError(null);
    track('Waitlist Submit', {});
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          favoriteArtist: values.favoriteArtist ?? '',
          email: values.email,
          hp: values.company ?? '',
          source: 'nyx-landing-2026',
        }),
      });
      if (res.ok) {
        let alreadyOnList = false;
        try {
          const body = (await res.clone().json()) as { alreadyOnList?: boolean };
          alreadyOnList = body.alreadyOnList === true;
        } catch {
          /* response wasn't JSON — treat as fresh signup */
        }
        track('Waitlist Success', { alreadyOnList });
        setState('success');
        reset({ favoriteArtist: '', email: '', company: '' });
        return;
      }
      // Surface the server's reason if available.
      let message: string = waitlistCopy.errorGeneric;
      try {
        const body = (await res.json()) as { message?: string };
        if (body.message) message = body.message;
      } catch {
        /* ignore */
      }
      if (res.status === 429) {
        message = 'Demasiados intentos. Probá en un par de minutos.';
      }
      track('Waitlist Error', { reason: `http_${res.status}` });
      setServerError(message);
      setState('error');
    } catch {
      track('Waitlist Error', { reason: 'network' });
      setServerError(waitlistCopy.errorGeneric);
      setState('error');
    }
  };

  return (
    <section id="waitlist" className="nl-waitlist" aria-labelledby="waitlist-heading">
      <div className="nl-waitlist-grid">
        <div>
          <Reveal as="div" className="nl-waitlist-eyebrow">
            {waitlistCopy.eyebrow}
          </Reveal>
          <Reveal
            as="h2"
            className="nl-waitlist-title"
            id="waitlist-heading"
            dangerouslySetInnerHTML={{ __html: waitlistCopy.title }}
          />
          <Reveal as="p" className="nl-waitlist-lede">
            {waitlistCopy.lede}
          </Reveal>
          <Reveal as="div" className="nl-waitlist-meta">
            {waitlistCopy.meta.map((row) => (
              <div key={row.label} className="nl-meta-row">
                <span>{row.label}</span>
                <div>{row.value}</div>
              </div>
            ))}
          </Reveal>
        </div>

        <Reveal
          as="form"
          className="nl-form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          aria-describedby="waitlist-fine"
        >
          <div className="nl-form-eyebrow">{waitlistCopy.formEyebrow}</div>

          <div className="nl-form-row">
            <label htmlFor="wl-favorite-artist">{waitlistCopy.fields.favoriteArtist.label}</label>
            <input
              id="wl-favorite-artist"
              className="nl-form-input"
              placeholder={waitlistCopy.fields.favoriteArtist.placeholder}
              {...register('favoriteArtist')}
            />
          </div>

          <div className="nl-form-row">
            <label htmlFor="wl-email">{waitlistCopy.fields.email.label}</label>
            <input
              id="wl-email"
              type="email"
              required
              className={`nl-form-input ${errors.email ? 'is-error' : ''}`}
              placeholder={waitlistCopy.fields.email.placeholder}
              autoComplete="email"
              aria-invalid={errors.email ? 'true' : undefined}
              aria-describedby={errors.email ? 'wl-email-error' : undefined}
              {...register('email')}
            />
            {errors.email && (
              <div className="nl-form-error" id="wl-email-error">
                {errors.email.message ?? waitlistCopy.errorEmail}
              </div>
            )}
          </div>

          {/* Honeypot — visually hidden + aria-hidden. */}
          <div className="nl-form-honeypot" aria-hidden="true">
            <label htmlFor="wl-company">Company (do not fill)</label>
            <input id="wl-company" type="text" tabIndex={-1} autoComplete="off" {...register('company')} />
          </div>

          {serverError && state === 'error' && (
            <div className="nl-form-error" role="alert">
              {serverError}
            </div>
          )}

          <div className="nl-form-state" data-state={state}>
            <button
              type="submit"
              disabled={isSubmitting || state === 'success'}
              className={`nl-form-submit ${state === 'success' ? 'is-success' : ''}`}
            >
              {state === 'success'
                ? waitlistCopy.submitSent
                : state === 'sending' || isSubmitting
                  ? waitlistCopy.submitSending
                  : waitlistCopy.submit}
              {state !== 'success' && <span aria-hidden="true">→</span>}
            </button>
          </div>

          <p className="nl-form-fine" id="waitlist-fine">
            {waitlistCopy.fine}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
